import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import {
  EyeIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AePageHeader, AePageShell, AeIconButton } from '@/components/assessment-engine/layout';
import {
  AE_BTN_PRIMARY,
  AE_CARD,
  AE_ICON_BTN,
  AE_INPUT,
  AE_LABEL,
  AE_LINK,
  AE_MUTED,
  AE_SELECT,
  AE_STATUS_BADGE,
  AE_STATUS_DRAFT_TEXT,
  AE_ICON_SIZE,
} from '@/components/assessment-engine/layout/aeTheme';
import {
  campaignPeriodGranularity,
  campaignResponseModeLabelKey,
  normalizeCampaignListItem,
} from '@/components/assessment-engine/campaignResponseMode';
import {
  formatCampaignListDate,
  getAssessmentApiErrorMessage,
  campaignMatchesStatusFilter,
  mergeCampaignLifecycleIntoListItem,
} from '@/components/assessment-engine/campaigns/campaignListUtils';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaign, AeCampaignListItem, AeCampaignStatus } from '@/types/assessmentEngine';

const STATUS_FILTER_OPTIONS: Array<AeCampaignStatus | 'all'> = ['all', 'draft', 'open', 'closed'];

const LIST_ICON_BTN = `${AE_ICON_BTN} !h-8 !w-8 !p-1 shrink-0 box-border`;

/** Tenant administrator — campaign list with filters, metadata columns, and row actions. */
export const CampaignListPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<AeCampaignListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<AeCampaignStatus | 'all'>('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [administratorOptions, setAdministratorOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const loadGenerationRef = useRef(0);

  const loadAdministratorOptions = useCallback(() => {
    assessmentEngineService
      .listCampaigns()
      .then((rows) => {
        const normalized = rows.map(normalizeCampaignListItem);
        const map = new Map<string, string>();
        for (const c of normalized) {
          if (c.administered_by && c.administrator_name) {
            map.set(c.administered_by, c.administrator_name);
          }
        }
        setAdministratorOptions(
          Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => toast.error(t('assessmentEngine.campaigns.failedLoad')));
  }, [t]);

  useEffect(() => {
    loadAdministratorOptions();
  }, [loadAdministratorOptions]);

  const load = useCallback((options?: { silent?: boolean }): Promise<void> => {
    const generation = ++loadGenerationRef.current;
    if (!options?.silent) {
      setLoading(true);
    }
    return assessmentEngineService
      .listCampaigns({
        status: statusFilter === 'all' ? undefined : statusFilter,
        company: companyFilter.trim() || undefined,
        administered_by: adminFilter || undefined,
      })
      .then((rows) => {
        if (generation !== loadGenerationRef.current) return;
        setCampaigns(rows.map(normalizeCampaignListItem));
      })
      .catch(() => {
        if (generation === loadGenerationRef.current) {
          toast.error(t('assessmentEngine.campaigns.failedLoad'));
        }
      })
      .finally(() => {
        if (generation === loadGenerationRef.current && !options?.silent) {
          setLoading(false);
        }
      });
  }, [statusFilter, companyFilter, adminFilter, t]);

  useEffect(() => {
    const timer = window.setTimeout(load, companyFilter ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, companyFilter]);

  const runLifecycle = async (
    campaign: AeCampaignListItem,
    action: 'open' | 'close' | 'reopen'
  ) => {
    setBusyId(campaign.id);
    try {
      let updated: AeCampaign;
      let invitesSent = 0;
      if (action === 'open') {
        const result = await assessmentEngineService.openCampaign(campaign.id);
        updated = result.campaign;
        invitesSent = result.invitesSent;
      } else if (action === 'close') {
        updated = await assessmentEngineService.closeCampaign(campaign.id);
      } else {
        const result = await assessmentEngineService.reopenCampaign(campaign.id);
        updated = result.campaign;
        invitesSent = result.invitesSent;
      }

      setCampaigns((rows) => {
        if (!campaignMatchesStatusFilter(updated.status, statusFilter)) {
          return rows.filter((row) => row.id !== campaign.id);
        }
        return rows.map((row) =>
          row.id === campaign.id ? mergeCampaignLifecycleIntoListItem(row, updated) : row
        );
      });

      if (invitesSent > 0) {
        toast.success(
          t('assessmentEngine.hub.invitesSentOnOpen', { count: invitesSent })
        );
      } else {
        toast.success(t('assessmentEngine.campaigns.lifecycleUpdated'));
      }
      await load({ silent: true });
    } catch (error) {
      const fallback =
        action === 'close'
          ? t('assessmentEngine.campaigns.closeFailed')
          : t('assessmentEngine.campaigns.openFailed');
      toast.error(getAssessmentApiErrorMessage(error, fallback));
    } finally {
      setBusyId(null);
    }
  };

  const deleteCampaign = async (campaign: AeCampaignListItem) => {
    if (!window.confirm(t('assessmentEngine.campaigns.deleteConfirm', { name: campaign.name }))) {
      return;
    }
    setBusyId(campaign.id);
    try {
      await assessmentEngineService.deleteCampaign(campaign.id);
      toast.success(t('assessmentEngine.campaigns.deleted'));
      setCampaigns((rows) => rows.filter((row) => row.id !== campaign.id));
    } catch (error) {
      toast.error(getAssessmentApiErrorMessage(error, t('assessmentEngine.campaigns.deleteFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AePageShell>
      <AePageHeader
        title={t('assessmentEngine.campaigns.title')}
        actions={
          <button
            type="button"
            className={AE_BTN_PRIMARY}
            onClick={() => navigate('/assessment-engine/campaigns/new')}
          >
            <PlusIcon className="h-5 w-5" />
            {t('assessmentEngine.campaigns.newCampaign')}
          </button>
        }
      />

      <div className={`${AE_CARD} grid grid-cols-1 md:grid-cols-3 gap-4 mb-4`}>
        <div>
          <label className={AE_LABEL} htmlFor="ae-campaign-status-filter">
            {t('assessmentEngine.campaigns.filterStatus')}
          </label>
          <select
            id="ae-campaign-status-filter"
            className={AE_SELECT}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AeCampaignStatus | 'all')}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt === 'all'
                  ? t('assessmentEngine.campaigns.filterStatusAll')
                  : t(`assessmentEngine.status.${opt}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={AE_LABEL} htmlFor="ae-campaign-company-filter">
            {t('assessmentEngine.campaigns.filterCompany')}
          </label>
          <input
            id="ae-campaign-company-filter"
            type="search"
            className={AE_INPUT}
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            placeholder={t('assessmentEngine.campaigns.filterCompanyPlaceholder')}
          />
        </div>
        <div>
          <label className={AE_LABEL} htmlFor="ae-campaign-admin-filter">
            {t('assessmentEngine.campaigns.filterAdministrator')}
          </label>
          <select
            id="ae-campaign-admin-filter"
            className={AE_SELECT}
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
          >
            <option value="">{t('assessmentEngine.campaigns.filterAdministratorAll')}</option>
            {administratorOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className={AE_MUTED}>{t('assessmentEngine.campaigns.loading')}</p>
      ) : campaigns.length === 0 ? (
        <p className={AE_MUTED}>{t('assessmentEngine.campaigns.empty')}</p>
      ) : (
        <div className={`${AE_CARD} overflow-x-auto`}>
          <table className="w-full text-sm min-w-[64rem]">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-secondary-text)]">
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colId')}</th>
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colName')}</th>
                <th className="py-3 pr-3 font-medium whitespace-nowrap">
                  {t('assessmentEngine.campaigns.colCreated')}
                </th>
                <th className="py-3 pr-3 font-medium whitespace-nowrap">
                  {t('assessmentEngine.campaigns.colUpdated')}
                </th>
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colCompany')}</th>
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colStatus')}</th>
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colFrequency')}</th>
                <th className="py-3 pr-3 font-medium text-right">
                  {t('assessmentEngine.campaigns.colInvites')}
                </th>
                <th className="py-3 pr-3 font-medium text-right">
                  {t('assessmentEngine.campaigns.colResponses')}
                </th>
                <th className="py-3 pr-3 font-medium">{t('assessmentEngine.campaigns.colAdministrator')}</th>
                <th className="py-3 font-medium w-40">
                  <span className="sr-only">{t('assessmentEngine.campaigns.colActions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const busy = busyId === c.id;
                const hubPath = `/assessment-engine/campaigns/${c.id}`;
                const setupPath = `${hubPath}?tab=basics`;

                return (
                  <tr
                    key={c.id}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-hover-bg)] align-top"
                  >
                    <td className="py-3 pr-3 font-mono text-xs text-[var(--color-secondary-text)] max-w-[8.5rem] break-all">
                      {c.id}
                    </td>
                    <td className="py-3 pr-3 min-w-[10rem]">
                      <Link to={hubPath} className={AE_LINK}>
                        {c.name}
                      </Link>
                      <p className="text-xs text-[var(--color-secondary-text)] mt-0.5">
                        {t('assessmentEngine.campaigns.colTemplateId')}:{' '}
                        <span className="font-mono break-all">{c.template_id}</span>
                        {c.template_name ? (
                          <span className="block mt-0.5">{c.template_name}</span>
                        ) : null}
                      </p>
                    </td>
                    <td className="py-3 pr-3 text-[var(--color-surface-text)] whitespace-nowrap tabular-nums">
                      {formatCampaignListDate(c.created_at, currentLanguage.id)}
                    </td>
                    <td className="py-3 pr-3 text-[var(--color-surface-text)] whitespace-nowrap tabular-nums">
                      {formatCampaignListDate(c.updated_at, currentLanguage.id)}
                    </td>
                    <td className="py-3 pr-3 text-[var(--color-surface-text)]">
                      {c.company_name ?? '—'}
                    </td>
                    <td className="py-3 pr-3">
                      {c.status === 'draft' ? (
                        <span className={AE_STATUS_DRAFT_TEXT}>
                          {t('assessmentEngine.status.draft')}
                        </span>
                      ) : (
                        <span
                          className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                            AE_STATUS_BADGE[c.status] ?? AE_STATUS_BADGE.draft
                          }`}
                        >
                          {t(`assessmentEngine.status.${c.status}`)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-[var(--color-surface-text)] whitespace-nowrap">
                      {(() => {
                        const labelKey = campaignResponseModeLabelKey(c);
                        if (labelKey === 'assessmentEngine.responseMode.perPeriodWithGranularity') {
                          const granularity = campaignPeriodGranularity(c.time_period_config);
                          return t(labelKey, {
                            period: granularity
                              ? t(`assessmentEngine.period.${granularity}`)
                              : t('assessmentEngine.responseMode.per_period'),
                          });
                        }
                        return t(labelKey);
                      })()}
                    </td>
                    <td className="py-3 pr-3 text-right text-[var(--color-surface-text)] tabular-nums">
                      {c.invite_count ?? 0}
                    </td>
                    <td className="py-3 pr-3 text-right text-[var(--color-surface-text)] tabular-nums">
                      {c.complete_count ?? c.response_count ?? 0}
                    </td>
                    <td className="py-3 pr-3 text-[var(--color-surface-text)]">
                      {c.administrator_name ?? '—'}
                    </td>
                    <td className="py-3">
                      <div className="flex justify-end flex-wrap gap-1">
                        <Link
                          to={hubPath}
                          className={LIST_ICON_BTN}
                          title={t('assessmentEngine.campaigns.actionView')}
                          aria-label={t('assessmentEngine.campaigns.actionView')}
                        >
                          <EyeIcon className={AE_ICON_SIZE} aria-hidden />
                        </Link>
                        <Link
                          to={setupPath}
                          className={LIST_ICON_BTN}
                          title={t('assessmentEngine.campaigns.actionEdit')}
                          aria-label={t('assessmentEngine.campaigns.actionEdit')}
                        >
                          <PencilSquareIcon className={AE_ICON_SIZE} aria-hidden />
                        </Link>
                        {c.status === 'draft' && (
                          <AeIconButton
                            compact
                            label={t('assessmentEngine.campaigns.actionOpen')}
                            variant="success"
                            disabled={busy}
                            onClick={() => void runLifecycle(c, 'open')}
                          >
                            <LockOpenIcon className={AE_ICON_SIZE} aria-hidden />
                          </AeIconButton>
                        )}
                        {c.status === 'open' && (
                          <AeIconButton
                            compact
                            label={t('assessmentEngine.campaigns.actionClose')}
                            variant="warning"
                            disabled={busy}
                            onClick={() => void runLifecycle(c, 'close')}
                          >
                            <LockClosedIcon className={AE_ICON_SIZE} aria-hidden />
                          </AeIconButton>
                        )}
                        {c.status === 'closed' && (
                          <AeIconButton
                            compact
                            label={t('assessmentEngine.campaigns.actionReopen')}
                            variant="success"
                            disabled={busy}
                            onClick={() => void runLifecycle(c, 'reopen')}
                          >
                            <LockOpenIcon className={AE_ICON_SIZE} aria-hidden />
                          </AeIconButton>
                        )}
                        <AeIconButton
                          compact
                          label={t('assessmentEngine.campaigns.delete')}
                          variant="danger"
                          disabled={busy}
                          onClick={() => void deleteCampaign(c)}
                        >
                          <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                        </AeIconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AePageShell>
  );
};
